import React, { Component } from 'react';
import { Router } from '@reach/router';
import ConnectionManager from 'ConnectionManager';
import { ApolloProvider } from '@apollo/react-hooks';

import { AuthenticationFailedError } from '@pearsontechnology/aero-rtm';
import { Spinner } from '@pearsontechnology/aero-components-learnspace';
import apolloClient from 'ConnectionManager/apolloClient';
import NotFoundPage from 'components/NotFoundPage';
import PluginContainer from 'components/PluginContainer';
import AuthManager from '../AuthManager';
import { AppWrapper } from './styles';
// Import is required for GTM to fire global events
// eslint-disable-next-line
import gtm from '../utils/gtm';
import gtmService from '../utils/gtm/gtmService';
import UserService from '../services/UserService/UserService';
import { subscribeToEvent } from '../services/EventService/EventServiceProvider';
import { setTrackingUser } from '../cookies/FullStory';
import { logNewrelicAddPageAction } from '../cookies/NewRelic';

const authenticationStates = {
  AUTHENTICATED: 'AUTHENTICATED',
  NO_CREDENTIALS: 'NO_CREDENTIALS',
  LOADING_CREDENTIALS: 'LOADING_CREDENTIALS',
  OTHER: 'OTHER',
};

class App extends Component {
  state = {
    authentication: authenticationStates.LOADING_CREDENTIALS,
    authenticationError: undefined,
    pluginContainerKey: 0, // used to guarantee the PluginContainer will be re-mount restarting the lesson
  };

  unsubscribeActivityRestartEvent = undefined;

  componentDidMount() {
    this.connectRTM();
    ConnectionManager.setLogoutCallback(this.handleLogout);
    this.unsubscribeActivityRestartEvent = subscribeToEvent('activityRestartEvent', this.activityRestart);
  }

  componentWillUnmount() {
    if (this.unsubscribeActivityRestartEvent) { this.unsubscribeActivityRestartEvent(); }
  }

  activityRestart = () => {
    const { pluginContainerKey } = this.state;
    this.setState({ pluginContainerKey: pluginContainerKey + 1 });
  };


  connectRTM = async () => {
    if (ConnectionManager.hasCredentials()) {
      ConnectionManager.openConnection()
        .then(() => {
          this.setState({
            authentication: authenticationStates.AUTHENTICATED,
            authenticationError: undefined,
          });

          const { piSession } = window;
          const piUserId = piSession.userId();
          const eventName = 'websocketOpenConnection';
          const eventData = { piUserId, description: 'Opened websocket connection via connectRTM' };
          logNewrelicAddPageAction(eventName, eventData);
        })
        .then(() => UserService.build().getUser())
        .then((user) => {
          setTrackingUser(user);
          return user;
        })
        .then(({ roles }) => ({
          roles,
          subscriptionId: 'NA',
        }))
        .then((user) => gtmService.pushUserParams(user))
        .catch((error) => {
          if (error.constructor === AuthenticationFailedError) {
            this.setState({
              authentication: authenticationStates.NO_CREDENTIALS,
              authenticationError: error,
            });
          } else {
            this.setState({
              authentication: authenticationStates.OTHER,
              authenticationError: error,
            });
          }
        });
    } else {
      AuthManager.init(() => {
        // setTimeout to stop exceeding maximum call stack as this gets called recursively
        setTimeout(() => {
          window.piSession.getToken((status, pearsonToken) => {
            const pearsonUid = window.piSession.userId();
            this.handleSso({
              pearsonUid,
              pearsonToken,
            });
          });
        }, 100);
      });
    }
  };

  handleSso = ({ pearsonUid, pearsonToken }) => {
    const credentials = { pearsonUid, pearsonToken };

    if (pearsonUid && pearsonToken) {
      ConnectionManager.setCredentials(credentials);
    }

    // recursively call auth flow until piSession is parsed
    this.connectRTM();
  };

  handleLogout = () => {
    this.setState({
      authentication: authenticationStates.NO_CREDENTIALS,
    });
    gtmService.pushUserLogoutEvent();
  };

  render() {
    const { authentication, authenticationError, pluginContainerKey } = this.state;

    if (authentication === authenticationStates.OTHER) {
      throw authenticationError;
    }

    if (authentication === authenticationStates.LOADING_CREDENTIALS) {
      // TODO: return loading component
      return null;
    }

    if (authentication === authenticationStates.NO_CREDENTIALS) {
      return (
        <AppWrapper>
          <NotFoundPage />
        </AppWrapper>
      );
    }

    // AUTHENTICATED
    return (
      <AppWrapper>
        <ApolloProvider client={apolloClient}>
          <Router>
            <PluginContainer path="/:cohortId/:deploymentId/*" key={pluginContainerKey} />
            <Spinner path="/loadingEvaluation" style={{ top: '40vh' }} />
            <NotFoundPage default />
          </Router>
        </ApolloProvider>
      </AppWrapper>
    );
  }
}

export default App;
